<template>
  <NavPanelLink
    v-if="shouldShowChat"
    :active="chatSidebarOpen"
    :class="{[$style.navExpanded]: expanded}"
    isButton
    :item="chatItem"
    :panelExpanded="expanded"
    @click="openChatApp"
  >
    <template v-slot:default>
      <DaysRemainingBadge
        :class="$style.remainingBadge"
        :comparisonDate="getOrgCreatedDate"
        :expanded="expanded"
        :trialLength="trialLength"
      />
      <div
        v-show="hasNewMessage"
        :class="[
          $style.newMessage,
          {[$style.newMessageExpanded]: expanded },
        ]"
        title="new message"
      />
    </template>
    <template v-slot:popover>
      <NavPopover
        v-if="showPopover"
        arrowPlacement="bottom"
        :bodyText="popoverBodyText"
        buttonText="Let's Chat"
        :class="{
          [$style.popover]: true,
          [$style.popoverExpanded]: expanded,
        }"
        :handleAction="handleOpenChatPopover"
        :handleClose="handleClose"
        :headerText="popoverHeaderText"
        isButton
        placement="right-start"
      />
    </template>
  </NavPanelLink>
</template>

<script>
import { ChatConsts } from 'client/util/Constants';
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import {
  intercomHide,
  intercomLoad,
  intercomScript,
  intercomSetCloseCallback,
  intercomSetMessageUpdateCallback,
  intercomSetShowCallback,
  intercomShow,
} from 'client/GrowthEngineering/utilities/intercomScript';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import LoadingIcon from 'components/LoadingOverlay/LoadingIcon.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import NavPanelLink from 'client/components/NavPanel/NavPanelLink.vue';
import NavPanelLinkListItem from 'client/components/NavPanel/NavPanelLinkListItem';
import NavPopover from 'client/components/NavPanel/NavPopover.vue';
import iconChat from 'public/img/icons/nav/icon-nav-chat.svg?inline';

import DaysRemainingBadge from './DaysRemainingBadge.vue';

const {
  description,
  location,
  section,
  text,
} = eventProperties;

const {
  delayTime,
  popoverBodyText,
  popoverHeaderText,
  trialLength,
} = ChatConsts;
const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';

export default {
  name: 'IntercomChat',

  components: {
    DaysRemainingBadge,
    NavPopover,
    NavPanelLink,
  },

  props: {
    expanded: Boolean,

    imageComponent: {
      type: Object,
      default() {
        return iconChat;
      },
    },
  },

  data() {
    return {
      chatSidebarOpen: false,
      chatLoaded: false,
      chatMessage: {},
      delayTime,
      hasNewMessage: false,
      popoverBodyText,
      popoverHeaderText,
      shouldShowChat: false,
      showPopover: false,
      trialLength,
      isChatOpen: false, // If we implement intercom elsewhere, move to store
      isChatLoading: true,
      intercomLoaded: false,
      error: false,
    };
  },

  computed: {
    ...mapGetters('OrganizationsCollection', ['getOrgCreatedDate']),

    ...mapGetters(onboardingModule, {
      onboardingFlowComplete: 'getOnboardingFlowComplete',
    }),

    ...mapGetters('PlanModule', ['planHasFeature']),

    ...mapGetters('SettingsModule', ['getIsCustomer']),

    ...mapState('SettingsModule', [
      'orgId',
      'user',
      'supportLevel',
    ]),

    ...mapState(growthModule, [
      'isMobile',
    ]),

    ...mapState(onboardingModule, [
      'adminGrowthData',
    ]),

    chatItem() {
      return new NavPanelLinkListItem({
        imageComponent: this.isChatLoading ? LoadingIcon : this.imageComponent,
        title: 'Live Chat',
      });
    },

    chatSeen() {
      return this.adminGrowthData.onboardingState.chatSeen;
    },
  },

  watch: {
    onboardingFlowComplete(newValue) {
      if (newValue === true) {
        this.autoOpenPopover();
      }
    },
  },

  created() {
    this.shouldShowChat = (
      !!this.orgId
    && !!this.getOrgCreatedDate);
  },

  async mounted() {
    if (this.shouldShowChat) {
      await this.fetchEntitledFeatureCodes();
      this.chatSetup();
      this.autoOpenPopover();
    }
  },

  methods: {
    ...mapActions(onboardingModule, [
      'updateChatSeen',
    ]),

    ...mapActions('PlanModule', [
      'fetchEntitledFeatureCodes',
    ]),

    autoOpenPopover() {
      // Opens Chat Popover After 2 minutes, if chat isn't open
      // and chat was not seen before
      const shouldOpenNewChat = !this.chatSeen
      && !this.isChatOpen
      && !this.isMobile
      && this.onboardingFlowComplete;

      if (shouldOpenNewChat) {
        this.clearDelayOpening();
        this.delayOpening = setTimeout(this.openPopover, this.delayTime);
      }
    },

    chatSetup() {
      intercomScript(() => {
        this.isChatLoading = false;
      });

      intercomSetCloseCallback(() => {
        this.updateChatSeen(true);
        this.isChatOpen = false;
      });

      intercomSetMessageUpdateCallback((unreadMessageCount) => {
        this.intercomLoaded = true;
        if (unreadMessageCount > 0) {
          this.updateHasNewMessage();
        }
      });

      intercomSetShowCallback(() => {
        this.isChatOpen = true;
        this.updateChatSeen(true);
      });

      setTimeout(() => {
        if (this.intercomLoaded === false) {
          this.isChatLoading = false;
          this.error = true;
        }
      }, 10000);
    },

    clearDelayOpening() {
      if (this.delayOpening) clearTimeout(this.delayOpening);
    },

    handleClose() {
      this.showPopover = false;
      this.updateChatSeen(true);
      EventHelper.triggerButtonClicked({
        [description]: 'Chat Popover Closed',
        [location]: 'Side Nav Panel',
        [section]: 'Chat',
        [text]: 'Close',
      });
      intercomHide();
    },

    handleOpenChatPopover() {
      EventHelper.triggerButtonClicked({
        [description]: 'Chat Popover Clicked',
        [location]: 'Side Nav Panel',
        [section]: 'Chat',
        [text]: "Let's Chat",
      });
      this.openChatApp();
    },

    loadChat() {
      if (this.chatLoaded) return;
      const hasPremiumSupport = this.getIsCustomer && this.planHasFeature('premiumSupport');
      const email = this.user && this.user.email;

      intercomLoad({
        email,
        orgId: this.orgId,
        hasSupport: hasPremiumSupport,
        signupDate: this.getOrgCreatedDate,
        supportLevel: this.supportLevel,
      });
      this.chatLoaded = true;
    },

    openChatApp() {
      this.clearDelayOpening();
      this.loadChat();
      this.showPopover = false;

      if (this.error) {
        this.updateShowError();
        return;
      }
      this.hasNewMessage = false;
      intercomShow();
    },

    openPopover() {
      this.showPopover = true;
    },

    updateHasNewMessage() {
      this.hasNewMessage = true;
    },

    updateShowError() {
      if (!this.isChatOpen) {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            cancelText: '',
            continueText: 'ok, got it',
            headerText: 'It looks like you’re using a content blocker!',
            mdTemplate: 'We’ve detected that you’re using a content blocking plugin for your browser.\n\nTo access Live Chat, go to your content blocking plugin and add jumpcloud.com as an exception.',
          },
        });
      }
    },
  },
};
</script>

<style module>
@value jcError from "~css/brand-colors.css";
@value (max-width-large) from '~css/breakpoints.css';
@value (tooltipZIndex) from '~css/z-index.css';
@value (panelSpeedSm, panelTimingFunction) from '~css/transitions.css';
@value newMessageSize: 4px;

.newMessage {
  background-color: jcError;
  border-radius: newMessageSize;
  height: newMessageSize;
  left: 32px;
  position: absolute;
  top: 8px;
  transition: top 0.4s linear;
  width: newMessageSize;
}

.popover {
  height: 168px;
  left: 112px;
  margin-top: -240px;
  padding: 1rem;
  position: fixed;
  top: auto;
  width: 168px;
  z-index: tooltipZIndex;
}

.popoverExpanded {
  left: 275px;
}

.remainingBadge {
  opacity: 0;
  position: absolute;
  right: var(--navPanelPadding);
  top: 2px;
  transition: all panelSpeedSm panelTimingFunction;
}

.navExpanded .remainingBadge {
  opacity: 1;
}
</style>
